import { z } from 'zod';

export const DecisionActionSchema = z.enum([
  'NO_ACTION',
  'RESEARCH_MORE',
  'WATCH',
  'INITIATE_STARTER',
  'ADD',
  'HOLD',
  'TRIM',
  'EXIT_THESIS_BROKEN',
  'EXIT_TARGET_REACHED',
  'RAISE_CASH',
  'REBALANCE',
]);

export type DecisionAction = z.infer<typeof DecisionActionSchema>;

/** Runtime enum object for DecisionAction values. */
export const DecisionAction = DecisionActionSchema.enum;

export const SetupTypeSchema = z.enum([
  'discount',
  'protected_node',
  'protected_discount',
  'policy_reconstruction',
  'country_access',
  'recovery_washout',
  'optionality',
  'event_driven',
  'tactical_lab',
]);

export type SetupType = z.infer<typeof SetupTypeSchema>;

export const DecisionScenarioSchema = z.object({
  scenarioId: z.string(),
  probability: z.number().min(0).max(1),
  portfolioImpact: z.number().nullable(),
  isBearCase: z.boolean().default(false),
});

export const InvestmentDecisionSchema = z.object({
  decisionId: z.string(),
  madeAt: z.string(),
  asOfDataCutoff: z.string(),
  action: DecisionActionSchema,
  securityId: z.string().optional(),
  portfolioId: z.string(),
  thesisId: z.string().optional(),
  setupType: SetupTypeSchema,
  horizonMonths: z.number().int().positive(),
  facts: z.array(z.string()).min(1),
  inferences: z.array(z.string()).default([]),
  opinion: z.string(),
  ownershipSummary: z.tuple([z.string(), z.string(), z.string()]),
  playerMapId: z.string().optional(),
  tpsAssessmentId: z.string().optional(),
  valuationRunIds: z.array(z.string()).default([]),
  scenarios: z.array(DecisionScenarioSchema).default([]),
  catalysts: z.array(z.string()).default([]),
  falsifiers: z.array(z.string()).min(1),
  leadingIndicators: z.array(z.string()).default([]),
  alternativesConsidered: z.array(z.string()).default([]),
  noActionAlternative: z.string(),
  sizing: z.object({
    proposedWeight: z.number().nullable(),
    maxLossBudget: z.number().nullable(),
    liquidityEffect: z.string(),
    concentrationEffect: z.string(),
  }),
  sourceIds: z.array(z.string()).min(1),
  evidenceCoveragePct: z.number().min(0).max(1),
  confidence: z.number().min(0).max(1),
  policyVersion: z.string(),
  humanConfirmed: z.boolean().default(false),
  thesisState: z.enum(['DRAFT', 'WATCHING', 'READY']).optional(),
});

export type InvestmentDecision = z.infer<typeof InvestmentDecisionSchema>;

export const OutcomeHorizonSchema = z.enum([
  '1m',
  '3m',
  '6m',
  '12m',
  '24m',
  '36m',
]);

export const OutcomeSchema = z.object({
  outcomeId: z.string(),
  decisionId: z.string(),
  horizon: OutcomeHorizonSchema,
  observedAt: z.string(),
  value: z.number().optional(),
  label: z.string().optional(),
  isCounterfactual: z.boolean().default(false),
  isProxy: z.boolean().default(false),
  sourceIds: z.array(z.string()).min(1),
});

export type Outcome = z.infer<typeof OutcomeSchema>;

/** @deprecated Use InvestmentDecisionSchema */
export const DecisionSchema = InvestmentDecisionSchema;
/** @deprecated Use InvestmentDecision */
export type Decision = InvestmentDecision;
/** @deprecated Use DecisionActionSchema */
export const ActionTypeSchema = DecisionActionSchema;
/** @deprecated Use DecisionAction */
export type ActionType = DecisionAction;
